/*
 * Promotion display for the product pages and the cart.
 *
 * Both promotions are applied by the YMQ B2B app, which keys them off
 * customer tags. That has one consequence this file exists to respect:
 * a shopper who is NOT logged in has no tags, so they get NEITHER the
 * member discount NOR the refill quantity tiers.
 *
 * Guests still see their live refill progress, because "you already have 3
 * — log in and they are 7% off" is the strongest reason to sign in. The
 * rule is about wording, not visibility: every figure shown to a guest
 * stays conditional. Telling someone a discount is applied when YMQ will
 * not apply it is the one failure mode worth engineering against, so the
 * locked and unlocked sentences are built in one place (progressText).
 *
 * Customers tagged 'business buyer' / 'early bird' get their own exclusive
 * price and are excluded from both promotions; Liquid renders no promo
 * markup at all for them, so this file simply finds nothing to drive.
 *
 * The member discount applies to the whole order. The quantity tiers apply
 * to refills only, so cart lines are classified by product type: anything
 * that is not a machine counts as a refill. Exclusion rather than an
 * allow-list means new refill types are picked up automatically.
 */
(function () {
  'use strict';

  function keywordsFrom(el) {
    var raw = (el.getAttribute('data-machine-types') || '').toLowerCase().split(',');
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var w = raw[i].replace(/^\s+|\s+$/g, '');
      if (w) out.push(w);
    }
    return out;
  }

  function tiersFrom(el) {
    var out = [];
    try {
      var parsed = JSON.parse(el.getAttribute('data-tiers') || '[]');
      for (var i = 0; i < parsed.length; i++) {
        if (parsed[i] && parsed[i].qty > 0) out.push(parsed[i]);
      }
    } catch (e) { return []; }
    out.sort(function (a, b) { return a.qty - b.qty; });
    return out;
  }

  function isRefill(item, keywords) {
    var type = (item.product_type || '').toLowerCase();
    for (var i = 0; i < keywords.length; i++) {
      if (type.indexOf(keywords[i]) !== -1) return false;
    }
    return true;
  }

  function countRefills(cart, keywords) {
    var items = (cart && cart.items) || [];
    var n = 0;
    for (var i = 0; i < items.length; i++) {
      if (isRefill(items[i], keywords)) n += items[i].quantity;
    }
    return n;
  }

  // Highest tier the count satisfies, and the next one still to reach.
  function resolve(count, tiers) {
    var reached = -1;
    for (var i = 0; i < tiers.length; i++) {
      if (count >= tiers[i].qty) reached = i;
    }
    var next = null;
    for (var j = 0; j < tiers.length; j++) {
      if (count < tiers[j].qty) { next = tiers[j]; break; }
    }
    return { reached: reached, next: next };
  }

  function plural(n) { return n === 1 ? '' : 's'; }

  /*
   * The progress sentence, shared by the product page and both cart
   * surfaces so the two can never drift apart.
   *
   * Guests DO see their progress — knowing "you already have 3, log in and
   * they are 7% off" is the whole reason to sign in. What they must never
   * see is that discount stated as already theirs, because YMQ will not
   * apply it until they are logged in and tagged. So every locked sentence
   * stays conditional ("log in to get"), and only an unlocked shopper is
   * told a discount is active.
   */
  function progressText(count, tiers, locked) {
    if (!tiers.length || count === 0) return null;
    var r = resolve(count, tiers);
    var head = count + ' refill' + plural(count) + ' in cart';
    if (r.reached >= 0) {
      if (locked) return head + ' — log in to get ' + tiers[r.reached].pct + '% off';
      if (r.next) return head + ' — add ' + (r.next.qty - count) + ' more for ' + r.next.pct + '% off';
      return head + ' — best price unlocked, ' + tiers[r.reached].pct + '% off';
    }
    var need = r.next.qty - count;
    if (locked) return head + ' — add ' + need + ' more and log in for ' + r.next.pct + '% off';
    return head + ' — add ' + need + ' more for ' + r.next.pct + '% off';
  }

  function setLine(el, text) {
    if (!el) return;
    if (text) { el.textContent = text; el.hidden = false; }
    else { el.hidden = true; }
  }

  // ── Product page: single-line tier row under the price ──────────────
  function renderProduct(root, count) {
    var locked = root.getAttribute('data-locked') === 'true';
    var tiers = tiersFrom(root);
    if (!tiers.length) return;
    var r = resolve(count, tiers);
    var segs = root.querySelectorAll('[data-nb-tier]');
    for (var i = 0; i < segs.length; i++) {
      // Locked shoppers get an outlined marker, not the filled one, so the
      // tier reads as "within reach" rather than "applied".
      segs[i].className = 'nb-promo__seg' +
        (i === r.reached ? (locked ? ' is-eligible' : ' is-active') : '');
    }
    setLine(root.parentNode.querySelector('[data-nb-tier-hint]'),
            progressText(count, tiers, locked));
  }

  // ── Cart drawer / cart page: refill progress line ───────────────────
  // The member discount line is static (it applies to the whole order and
  // never changes with cart contents); only the refill line is live.
  function renderCart(root, count) {
    var locked = root.getAttribute('data-locked') === 'true';
    var text = progressText(count, tiersFrom(root), locked);
    setLine(root.querySelector('[data-nb-promo-refill-line]'), text ? text + '.' : null);
  }

  function apply(cart) {
    var product = document.querySelector('[data-nb-promo-tiers]');
    var carts = document.querySelectorAll('[data-nb-promo-cart]');
    if (!product && !carts.length) return;
    var keywords = keywordsFrom(product || carts[0]);
    var count = countRefills(cart, keywords);
    if (product) renderProduct(product, count);
    for (var i = 0; i < carts.length; i++) renderCart(carts[i], count);
  }

  function refresh(cart) {
    if (cart) { apply(cart); return; }
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(apply)
      .catch(function () { /* leave the static markup as rendered */ });
  }

  document.addEventListener('niimbot:cart:updated', function (e) {
    refresh(e.detail && e.detail.cart);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(); });
  } else {
    refresh();
  }
})();
